import { Cartesian3 } from "cesium";
import type { ITrenchObjectData } from "./TrenchObject";
import {
	SegmentType,
	TrenchSavedData,
	type IControlPointRetrivedData,
	type ISlopeBreak,
	type TRubbingDestination,
	getAlignmentDataFromControlPointsAndSegmentTypes,
} from "earthbrain-viewer";
import {
	ObjectType,
	type ICrossSection,
	type ICrossSectionSettings,
	type IObjectData,
} from "./types";
import { defaultLineWorksCrossSection } from "@/components/Project/LineWorks/default";

export interface ITrenchObjectProps_Outdated {
	id: string;
	cid: string;
	type: number;
	name: string;
	isShow: boolean;
	isOpen: boolean;
	edit: boolean;
	panel: {
		trench: boolean;
		cut: boolean;
		volume: boolean;
	};
	cutVolume: number;
	transparency: number;
	info: {
		length: number;
		height: {
			min: number;
			max: number;
		};
		slope: {
			min: number;
			max: number;
		};
	};
	settings: {
		commonSettings: {
			width: number;
			leftSlope: number;
			rightSlope: number;
			isSuperElevation: boolean;
			interpolationPitch: number;
			surfaceColor: string;
			segmentSettings: [];
			specifiedElevation: number;
			longitudinalSlope: {
				slopeType: string;
				slopeValue: number;
				depth: number | null;
			};
			destination: {
				target: number;
				id: number;
				specifiedAltitude: number;
			};
		};
		cutSettings: {
			trenchHeight: number;
			moundSlope: number;
			shelfWidth: number;
			shelfSlope: number;
			bLeftRightSettings: boolean;
			generateShelf: boolean;
			leftSlopeHeight: number;
			leftMoundSlope: number;
			rightSlopeHeight: number;
			rightMoundSlope: number;
			leftShelfWidth: number;
			leftShelfSlope: number;
			rightShelfWidth: number;
			rightShelfSlope: number;
			color: string;
			outline: boolean;
		};
	};
	cash: {
		leftShelfWidth: number;
		rightShelfWidth: number;
	};
	json: string;
	isLoaded: boolean;
}

export interface ITrenchObjectData_OutDated extends IObjectData {
	id: string;
	name: string;
	panel: {
		trench: boolean;
		cut: boolean;
		volume: boolean;
	};
	cutVolume: number;
	transparency: number;
	length: number;
	heightRange: {
		min: number;
		max: number;
	};
	slopeRange: {
		min: number;
		max: number;
	};
	pathSettings: {
		width: number;
		leftSlope: number;
		rightSlope: number;
		isSuperElevation: boolean;
		interpolationPitch: number;
		surfaceColor: string;
		specifiedElevation: number;
		longitudinalSlope: {
			slopeType: string;
			slopeValue?: number;
			depth?: number | null;
		};
	};
	cutSettings: {
		trenchHeight: number;
		moundSlope: number;
		shelfWidth: number;
		shelfSlope: number;
		isLeftRightDifferent: boolean;
		generateShelf: boolean;
		leftSlopeHeight: number;
		leftMoundSlope: number;
		rightSlopeHeight: number;
		rightMoundSlope: number;
		leftShelfWidth: number;
		leftShelfSlope: number;
		rightShelfWidth: number;
		rightShelfSlope: number;
		color: string;
		outline: boolean;
	};
	associatedTerrain: { id: number; terrainType: string };

	controlPoints: IControlPointRetrivedData[];
	slopeBreaks: ISlopeBreak[];
	segmentTypes: SegmentType[];
	rubbingDestination: TRubbingDestination;
}

export interface ITrenchObjectData_Version1 extends ITrenchObjectData_OutDated {
	version: 1.0;
	cid: string;
	md5: string;
}

export interface ITrenchObjectData_Version2 extends ITrenchObjectData {
	version: 2.0;
	cid: string;
	md5: string;
}

type TTrenchSavedData =
	| ITrenchObjectData_Version1
	| ITrenchObjectData_Version2
	| ITrenchObjectProps_Outdated;

export class TrenchObjectAdapter {
	private static async fromProps_Outdated(
		data: ITrenchObjectProps_Outdated,
		json: string,
	): Promise<ITrenchObjectData> {
		const viewerTrenchData = await TrenchSavedData.parseTrenchSavedData(json);
		return {
			id: data.cid,
			type: ObjectType.TRENCH,
			md5: "",
			name: data.name,
			panel: data.panel,
			cutVolume: data.cutVolume,
			transparency: data.transparency,
			length: data.info.length,
			heightRange: data.info.height,
			slopeRange: data.info.slope,
			pathSettings: {
				width: data.settings.commonSettings.width,
				leftSlope: data.settings.commonSettings.leftSlope,
				rightSlope: data.settings.commonSettings.rightSlope,
				isSuperElevation: data.settings.commonSettings.isSuperElevation,
				interpolationPitch: data.settings.commonSettings.interpolationPitch,
				surfaceColor: data.settings.commonSettings.surfaceColor,
				specifiedElevation: data.settings.commonSettings.specifiedElevation,
				longitudinalSlope: data.settings.commonSettings.longitudinalSlope,
			},
			cutSettings: {
				trenchHeight: data.settings.cutSettings.trenchHeight,
				moundSlope: data.settings.cutSettings.moundSlope,
				shelfWidth: data.settings.cutSettings.shelfWidth,
				shelfSlope: data.settings.cutSettings.shelfSlope,
				isLeftRightDifferent: data.settings.cutSettings.bLeftRightSettings,
				generateShelf: data.settings.cutSettings.generateShelf,
				leftSlopeHeight: data.settings.cutSettings.leftSlopeHeight,
				leftMoundSlope: data.settings.cutSettings.leftMoundSlope,
				rightSlopeHeight: data.settings.cutSettings.rightSlopeHeight,
				rightMoundSlope: data.settings.cutSettings.rightMoundSlope,
				leftShelfWidth: data.settings.cutSettings.leftShelfWidth,
				leftShelfSlope: data.settings.cutSettings.leftShelfSlope,
				rightShelfWidth: data.settings.cutSettings.rightShelfWidth,
				rightShelfSlope: data.settings.cutSettings.rightShelfSlope,
				color: data.settings.cutSettings.color,
				outline: data.settings.cutSettings.outline,
			},
			associatedTerrain: {
				id: viewerTrenchData.associatedTerrain.id,
				terrainType: viewerTrenchData.associatedTerrain.terrainType,
			},
			surveyPoints: viewerTrenchData.surveyPoints,
			ipPointIds: viewerTrenchData.ipPointIds,
			horizontalAlignment: viewerTrenchData.horizontalAlignment,
			verticalAlignment: viewerTrenchData.verticalAlignment,
			rubbingDestination: viewerTrenchData.rubbingDestination,
		};
	}

	private static async fromTrenchData_Version1(
		data: ITrenchObjectData_Version1,
	): Promise<ITrenchObjectData> {
		const {
			surveyPoints,
			ipPointIds,
			horizontalAlignmentData,
			verticalAlignmentData,
		} = await getAlignmentDataFromControlPointsAndSegmentTypes(data);

		return {
			...data,
			surveyPoints,
			ipPointIds,
			horizontalAlignment: horizontalAlignmentData,
			verticalAlignment: verticalAlignmentData,
		};
	}

	private static fromTrenchData_Version2(
		data: ITrenchObjectData_Version2,
	): ITrenchObjectData {
		let crossSectionSettings: ICrossSectionSettings;
		const savedSettings: ICrossSection = (data as any).crossSection;
		if (savedSettings) {
			const defaultSettings = defaultLineWorksCrossSection();
			crossSectionSettings = {
				id: savedSettings.cross_section_id,
				leftWidth: savedSettings.left_side_width ?? defaultSettings.leftWidth,
				rightWidth:
					savedSettings.right_side_width ?? defaultSettings.rightWidth,
				interpolationPitch:
					savedSettings.station_interval ?? defaultSettings.interpolationPitch,
				samplingSize:
					savedSettings.sampling_size ?? defaultSettings.samplingSize,
			};
		}
		return {
			...data,
			surveyPoints: data.surveyPoints.map((sp) => ({
				...sp,
				wgs84Point: new Cartesian3(
					sp.wgs84Point.x,
					sp.wgs84Point.y,
					sp.wgs84Point.z,
				),
			})),
			crossSectionSetting: crossSectionSettings,
		};
	}

	public static async toTrenchData(
		data: TTrenchSavedData,
		json: string,
	): Promise<ITrenchObjectData> {
		if ("version" in data && data.version === 1.0) {
			return this.fromTrenchData_Version1(data as ITrenchObjectData_Version1);
		} else if ("version" in data && data.version === 2.0) {
			return this.fromTrenchData_Version2(data as ITrenchObjectData_Version2);
		} else {
			if (!json) {
				console.warn("json is not found");
				return undefined;
			}
			return this.fromProps_Outdated(data as ITrenchObjectProps_Outdated, json);
		}
	}
}
